import { useEffect, useMemo, useState } from 'react';
import { Form, message } from 'antd';
import { useAuthStore } from '@stores/auth.store';
import { useCampusBranches } from '../useCampusBranches';
import { useTenantClasses } from '../useTenantClasses';
import { useTenantSubjects } from '../useTenantSubjects';
import { subscribeClassTimetable, persistClassTimetable } from '@services/registry';

// Helper functions for time calculations
export const parseTimeToMinutes = (t) => {
    if (!t) return 0;
    const [h, m] = t.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
};

export const formatMinutesToTime = (mins) => {
    const h = Math.floor(mins / 60).toString().padStart(2, '0');
    const m = (mins % 60).toString().padStart(2, '0');
    return `${h}:${m}`;
};

function normalizeLoadedTimetable(daysData, activeKeys) {
    const result = {};
    let templateDay = null;
    for (const d of activeKeys) {
        if (Array.isArray(daysData?.[d]) && daysData[d].length > 0) {
            templateDay = d;
            break;
        }
    }

    const templateSlots = templateDay ? daysData[templateDay] : [];

    for (const d of activeKeys) {
        const currentSlots = daysData?.[d] || [];
        result[d] = templateSlots.map((ts, idx) => {
            const match = currentSlots.find((s) => s.id === ts.id) || currentSlots[idx];
            const isPeriod = (match?.type || 'period') === 'period';
            return {
                id: ts.id,
                type: ts.type || 'period',
                startTime: ts.startTime,
                endTime: ts.endTime,
                label: ts.label || '',
                subjectId: isPeriod ? (match?.subjectId || '') : '',
            };
        });
    }
    return result;
}

export function useTimetableScreen() {
    const tenantId = useAuthStore((s) => s.user?.tenantId);
    
    // Fetch data
    const { branches, loading: loadingCampuses } = useCampusBranches({ tenantId });
    const { rows: classes, loading: loadingClasses } = useTenantClasses({ tenantId });
    const { rows: subjects, loading: loadingSubjects } = useTenantSubjects({ tenantId });

    // Selections
    const [selectedCampusId, setSelectedCampusId] = useState(undefined);
    const [selectedClassId, setSelectedClassId] = useState(undefined);
    const [activeDay, setActiveDay] = useState('mon');

    // Local draft of timetable data
    const [draftTimetable, setDraftTimetable] = useState({}); // { mon: [...], tue: [...] }
    const [loadingTimetable, setLoadingTimetable] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // List of campuses
    const campusOptions = useMemo(() => {
        return (branches || [])
            .filter((b) => (b.branchName || '').trim())
            .map((b) => ({ value: b.id, label: b.branchName }));
    }, [branches]);

    const selectedCampus = useMemo(() => {
        return (branches || []).find((b) => b.id === selectedCampusId);
    }, [branches, selectedCampusId]);

    // List of classes for the selected campus
    const filteredClasses = useMemo(() => {
        if (!selectedCampusId) return [];
        return (classes || []).filter((c) => c.branchId === selectedCampusId && c.status !== 'inactive');
    }, [classes, selectedCampusId]);

    const selectedClass = useMemo(() => {
        return (classes || []).find((c) => c.id === selectedClassId);
    }, [classes, selectedClassId]);

    // Subjects belonging to the selected class
    const classSubjects = useMemo(() => {
        if (!selectedClassId) return [];
        return (subjects || []).filter((s) => Array.isArray(s.classIds) && s.classIds.includes(selectedClassId) && s.status !== 'inactive');
    }, [subjects, selectedClassId]);

    // Subject name mapping for display
    const subjectNameById = useMemo(() => {
        const m = new Map();
        for (const s of subjects || []) {
            m.set(s.id, s.name);
        }
        return m;
    }, [subjects]);

    // Reset selected class if campus changes
    useEffect(() => {
        setSelectedClassId(undefined);
        setDraftTimetable({});
    }, [selectedCampusId]);

    const activeKeys = useMemo(() => {
        return ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].filter((k) => {
            const daySched = selectedCampus?.schedule?.[k];
            return !!(daySched && daySched.on);
        });
    }, [selectedCampus]);

    // Subscribe to selected class timetable
    useEffect(() => {
        if (!tenantId || !selectedClassId) {
            setDraftTimetable({});
            setLoadingTimetable(false);
            return undefined;
        }

        setLoadingTimetable(true);
        const unsub = subscribeClassTimetable({
            tenantId,
            classId: selectedClassId,
            onNext: (data) => {
                const rawDays = data?.days || {};
                const normalized = normalizeLoadedTimetable(rawDays, activeKeys);
                setDraftTimetable(normalized);
                setLoadingTimetable(false);
            },
            onError: (err) => {
                console.error('Error fetching timetable:', err);
                message.error('Failed to load timetable.');
                setLoadingTimetable(false);
            }
        });

        return () => unsub();
    }, [tenantId, selectedClassId, activeKeys]);

    // Get campus operating hours for a specific day
    const getCampusDayHours = (dayKey) => {
        if (!selectedCampus) return null;
        const daySched = selectedCampus.schedule?.[dayKey];
        if (!daySched || !daySched.on) return null;
        return {
            from: daySched.from || '08:00',
            to: daySched.to || '14:00',
        };
    };

    // Propagate time changes down the slot sequence to maintain contiguity
    const propagateSlotsTime = (slots, dayHours) => {
        if (!slots || !slots.length) return [];
        const nextSlots = JSON.parse(JSON.stringify(slots));
        const schoolStartMins = parseTimeToMinutes(dayHours?.from || '08:00');
        const schoolEndMins = parseTimeToMinutes(dayHours?.to || '14:00');

        for (let i = 0; i < nextSlots.length; i++) {
            let startMins;
            if (i === 0) {
                // First slot starts either at user-set time or school start time
                startMins = parseTimeToMinutes(nextSlots[i].startTime) || schoolStartMins;
                if (startMins < schoolStartMins) startMins = schoolStartMins;
            } else {
                // Subsequent slots start exactly at the end time of previous slot
                startMins = parseTimeToMinutes(nextSlots[i - 1].endTime);
            }

            nextSlots[i].startTime = formatMinutesToTime(startMins);

            let endMins = parseTimeToMinutes(nextSlots[i].endTime);
            // Ensure end time is at least 5 minutes after start time
            if (endMins <= startMins) {
                endMins = startMins + 40; // Default 40 min period duration
            }

            // Clip to school end time
            if (endMins > schoolEndMins) {
                endMins = schoolEndMins;
            }

            nextSlots[i].endTime = formatMinutesToTime(endMins);
        }
        return nextSlots;
    };

    // Add a slot to ALL active days
    const addSlot = async (slotData) => {
        if (!activeKeys.length) return;
        const firstDay = activeKeys[0];
        const hours = getCampusDayHours(firstDay);
        if (!hours) return;

        const currentSlots = draftTimetable[firstDay] || [];
        const schoolStartMins = parseTimeToMinutes(hours.from);
        const schoolEndMins = parseTimeToMinutes(hours.to);

        let startMins = schoolStartMins;
        if (currentSlots.length > 0) {
            startMins = parseTimeToMinutes(currentSlots[currentSlots.length - 1].endTime);
        }

        if (startMins >= schoolEndMins) {
            message.warning('Timetable has already reached school closing time.');
            return;
        }

        let endMins = startMins + 40;
        if (endMins > schoolEndMins) {
            endMins = schoolEndMins;
        }

        const type = slotData?.type || 'period';
        const startTime = slotData?.startTime || formatMinutesToTime(startMins);
        const endTime = slotData?.endTime || formatMinutesToTime(endMins);
        const label = slotData?.label || '';

        const slotId = `slot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const nextDraft = { ...draftTimetable };
        for (const d of activeKeys) {
            const daySlots = draftTimetable[d] || [];
            nextDraft[d] = [...daySlots, {
                id: slotId,
                type,
                startTime,
                endTime,
                subjectId: '',
                label,
            }];
        }
        // Propagate to ensure contiguity
        for (const d of activeKeys) {
            const dayHours = getCampusDayHours(d);
            nextDraft[d] = propagateSlotsTime(nextDraft[d], dayHours);
        }
        setDraftTimetable(nextDraft);
        await handleSave(nextDraft);
    };

    // Remove a slot from ALL active days
    const removeSlot = async (slotId) => {
        const nextDraft = { ...draftTimetable };
        for (const d of activeKeys) {
            const daySlots = draftTimetable[d] || [];
            const filtered = daySlots.filter(s => s.id !== slotId);
            const hours = getCampusDayHours(d);
            nextDraft[d] = propagateSlotsTime(filtered, hours);
        }
        setDraftTimetable(nextDraft);
        await handleSave(nextDraft);
    };

    // Update a slot's timings/type/label across ALL active days
    const updateSlot = async (slotId, updates) => {
        const nextDraft = { ...draftTimetable };
        for (const d of activeKeys) {
            const daySlots = draftTimetable[d] || [];
            const updatedList = daySlots.map(s => {
                if (s.id === slotId) {
                    return { ...s, ...updates };
                }
                return s;
            });
            const hours = getCampusDayHours(d);
            nextDraft[d] = propagateSlotsTime(updatedList, hours);
        }
        setDraftTimetable(nextDraft);
        await handleSave(nextDraft);
    };

    // Update the subject of a specific slot on a specific day
    const updateSlotSubject = async (dayKey, slotId, subjectId) => {
        const nextDraft = { ...draftTimetable };
        const daySlots = draftTimetable[dayKey] || [];
        nextDraft[dayKey] = daySlots.map(s => {
            if (s.id === slotId) {
                return { ...s, subjectId };
            }
            return s;
        });
        setDraftTimetable(nextDraft);
        await handleSave(nextDraft);
    };

    // Clear all subjects for active day
    const clearDay = async (dayKey) => {
        const nextDraft = { ...draftTimetable };
        const daySlots = draftTimetable[dayKey] || [];
        nextDraft[dayKey] = daySlots.map(s => ({
            ...s,
            subjectId: '',
        }));
        setDraftTimetable(nextDraft);
        await handleSave(nextDraft);
    };

    // Save timetable to Firestore
    const handleSave = async (nextDraft = null) => {
        if (!tenantId || !selectedClassId) return;

        setIsSaving(true);
        try {
            await persistClassTimetable({
                tenantId,
                classId: selectedClassId,
                days: nextDraft || draftTimetable
            });
            message.success('Timetable saved successfully.');
        } catch (err) {
            console.error('Failed to save timetable:', err);
            message.error('Failed to save timetable.');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        loading: loadingCampuses || loadingClasses || loadingSubjects,
        loadingTimetable,
        isSaving,
        campusOptions,
        filteredClasses,
        selectedCampusId,
        setSelectedCampusId,
        selectedClassId,
        setSelectedClassId,
        selectedCampus,
        selectedClass,
        classSubjects,
        subjectNameById,
        activeDay,
        setActiveDay,
        draftTimetable,
        activeKeys,
        getCampusDayHours,
        addSlot,
        removeSlot,
        updateSlot,
        updateSlotSubject,
        handleSave,
        clearDay,
    };
}
